import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  X,
  Layers,
  ClipboardList,
  Pencil,
  Trash2,
  Plus,
  Save,
  Loader2,
} from "lucide-react";
import {
  getDepartmentsByBranch,
  createDepartment,
  updateDepartment,
  deleteDepartment,
} from "../../../api/hrm/departments/departmentInfo";
import {
  showConfirmDialog,
  showSuccessToast,
  showErrorToast,
} from "../../../components/ConfirmDialog";
import { hasPermission } from "../../../utils/permissionUtils";

const labelClass =
  "text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1";
const inputClass =
  "w-full bg-white border border-gray-300 rounded-2xl px-4 py-3 text-sm font-medium text-gray-800 outline-none transition-all hover:border-gray-400 focus:bg-white focus:border-red-500 focus:ring-4 focus:ring-red-500/5";

const Section = ({ icon: Icon, title, subTitle, children }) => (
  <section className="space-y-4 rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50/80 to-white p-4 sm:p-5">
    <div className="flex items-center gap-3">
      <div className="rounded-xl border border-red-700/10 bg-red-700/5 p-2 text-red-700">
        <Icon className="h-4 w-4 sm:h-5 sm:w-5" strokeWidth={2.25} />
      </div>
      <div className="min-w-0">
        <h3 className="text-sm font-black uppercase tracking-wide text-gray-800">
          {title}
        </h3>
        {subTitle ? (
          <p className="mt-0.5 text-[11px] text-gray-400">{subTitle}</p>
        ) : null}
      </div>
    </div>
    <div className="space-y-4">{children}</div>
  </section>
);

const Field = ({ label, required, children }) => (
  <div>
    <label className={`${labelClass} mb-1.5 block`}>
      {label}
      {required ? <span className="text-red-500"> *</span> : null}
    </label>
    {children}
  </div>
);

/**
 * Quản lý danh sách bộ phận thuộc một chi nhánh — UI mobile bottom-sheet.
 */
export default function BranchDepartmentsModal({ branch, isOpen, onClose }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const canCreate = hasPermission("hrm.branches", "C");
  const canUpdate = hasPermission("hrm.branches", "U");
  const canDelete =
    hasPermission("hrm.branches", "D") || hasPermission("hrm.branches", "U");

  const load = useCallback(async () => {
    if (!branch?.id) return;
    setLoading(true);
    try {
      const rows = await getDepartmentsByBranch(branch.id);
      setList(Array.isArray(rows) ? rows : []);
    } catch (e) {
      console.error(e);
      showErrorToast("Không thể tải danh sách bộ phận");
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [branch?.id]);

  useEffect(() => {
    if (isOpen && branch?.id) {
      setEditing(null);
      setName("");
      setDescription("");
      load();
    }
  }, [isOpen, branch?.id, load]);

  const resetForm = () => {
    setEditing(null);
    setName("");
    setDescription("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!branch?.id) return;
    const n = name.trim();
    if (!n) {
      showErrorToast("Nhập tên bộ phận");
      return;
    }

    try {
      setSaving(true);
      if (editing) {
        if (!canUpdate) return;
        await updateDepartment(editing.id, {
          name: n,
          description: description.trim() || null,
          branch_id: branch.id,
        });
        showSuccessToast("Đã cập nhật bộ phận");
      } else {
        if (!canCreate) return;
        await createDepartment({
          name: n,
          description: description.trim() || null,
          branch_id: branch.id,
        });
        showSuccessToast("Đã thêm bộ phận");
      }
      resetForm();
      await load();
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Không thể lưu bộ phận";
      showErrorToast(msg);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (row) => {
    if (!canUpdate) return;
    setEditing(row);
    setName(row.name || "");
    setDescription(row.description || "");
  };

  const handleDelete = async (row) => {
    if (!canDelete) return;
    const ok = await showConfirmDialog({
      title: "Xóa bộ phận?",
      text: `Xóa "${row.name}"? Không thể hoàn tác nếu còn nhân viên gắn bộ phận này.`,
      confirmText: "Xóa",
      cancelText: "Hủy",
    });
    if (!ok.isConfirmed) return;
    try {
      await deleteDepartment(row.id);
      showSuccessToast("Đã xóa bộ phận");
      if (editing?.id === row.id) resetForm();
      await load();
    } catch (err) {
      const msg =
        err?.response?.data?.message || err?.message || "Không thể xóa bộ phận";
      showErrorToast(msg);
    }
  };

  if (typeof document === "undefined") return null;
  if (!isOpen || !branch) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && branch && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-black/45 backdrop-blur-sm"
            onClick={onClose}
          />

          <div className="fixed inset-0 z-[210] flex items-end justify-center p-0 pointer-events-none sm:items-center sm:p-4">
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 320 }}
              role="dialog"
              aria-labelledby="branch-dept-title"
              className="pointer-events-auto flex h-[92dvh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[1.75rem] border border-gray-200 bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-[2rem]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 bg-gradient-to-r from-red-50/70 via-white to-white px-5 py-4 sm:px-6">
                <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm">
                    <Layers
                      className="h-5 w-5 sm:h-6 sm:w-6"
                      strokeWidth={2.25}
                    />
                  </div>
                  <div className="min-w-0">
                    <h2
                      id="branch-dept-title"
                      className="text-lg font-black uppercase tracking-tight text-gray-800 sm:text-xl"
                    >
                      Bộ phận <span className="text-red-700">chi nhánh</span>
                    </h2>
                    <p className="mt-0.5 truncate text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
                      {branch.name}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-gray-200 bg-white p-2 text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                  aria-label="Đóng"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Body */}
              <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain bg-slate-50/40 px-4 py-4 sm:space-y-5 sm:px-6 sm:py-5">
                <p className="text-[11px] leading-relaxed text-gray-500 sm:text-xs">
                  Mỗi chi nhánh có danh sách bộ phận riêng. Nhân viên và báo cáo
                  sẽ lọc theo bộ phận thuộc chi nhánh tương ứng.
                </p>

                {(canCreate || (editing && canUpdate)) && (
                  <Section
                    icon={Layers}
                    title={editing ? "Cập nhật bộ phận" : "Thêm bộ phận mới"}
                    subTitle={
                      editing
                        ? "Chỉnh sửa tên và mô tả, vẫn thuộc chi nhánh đang chọn."
                        : "Khai báo tên bộ phận (ví dụ: Kinh doanh, Kế toán...)."
                    }
                  >
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <Field label="Tên bộ phận" required>
                        <input
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Ví dụ: Kinh doanh, Kế toán..."
                          className={`${inputClass} font-semibold`}
                        />
                      </Field>
                      <Field label="Mô tả">
                        <textarea
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="Ghi chú (tuỳ chọn)"
                          rows={3}
                          className={`${inputClass} min-h-[88px] resize-none`}
                        />
                      </Field>
                      <div className="flex items-center gap-2 pt-1">
                        {editing ? (
                          <button
                            type="button"
                            onClick={resetForm}
                            className="flex-1 rounded-full border border-gray-200 bg-white px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-600 transition hover:border-gray-300 hover:bg-gray-50 sm:flex-none sm:px-6 sm:py-2.5"
                          >
                            Hủy sửa
                          </button>
                        ) : null}
                        <button
                          type="submit"
                          disabled={!name.trim() || saving}
                          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-gradient-to-r from-red-700 to-red-800 px-4 py-3 text-xs font-black uppercase tracking-wider text-white shadow-md shadow-red-100 transition hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none sm:px-6 sm:py-2.5"
                        >
                          {saving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : editing ? (
                            <Save size={16} />
                          ) : (
                            <Plus size={16} />
                          )}
                          {saving
                            ? "Đang xử lý..."
                            : editing
                              ? "Lưu thay đổi"
                              : "Thêm bộ phận"}
                        </button>
                      </div>
                    </form>
                  </Section>
                )}

                <Section
                  icon={ClipboardList}
                  title="Danh sách bộ phận"
                  subTitle={`${list.length} bộ phận tại chi nhánh này`}
                >
                  {loading ? (
                    <div className="flex items-center justify-center gap-3 py-10 text-gray-400">
                      <Loader2 className="h-5 w-5 animate-spin text-red-500" />
                      <span className="text-xs font-bold uppercase tracking-wider">
                        Đang tải...
                      </span>
                    </div>
                  ) : list.length === 0 ? (
                    <div className="rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50/80 px-4 py-8 text-center">
                      <ClipboardList className="mx-auto mb-2 h-8 w-8 text-gray-300" />
                      <p className="text-sm font-medium text-gray-400">
                        Chưa có bộ phận nào cho chi nhánh này.
                      </p>
                      <p className="mt-1 text-[11px] text-gray-400">
                        Thêm bộ phận ở phần phía trên (nếu bạn có quyền).
                      </p>
                    </div>
                  ) : (
                    <ul className="space-y-2.5">
                      {list.map((row, idx) => (
                        <li
                          key={row.id}
                          className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-white p-3.5 shadow-sm transition hover:border-red-200"
                        >
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-red-50 text-[11px] font-black text-red-600">
                            {String(idx + 1).padStart(2, "0")}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-bold tracking-tight text-gray-900">
                              {row.name}
                            </p>
                            {row.description ? (
                              <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-gray-500">
                                {row.description}
                              </p>
                            ) : (
                              <p className="mt-1 text-[10px] font-bold uppercase tracking-wider text-gray-300">
                                Chưa có mô tả
                              </p>
                            )}
                          </div>
                          <div className="flex shrink-0 items-center gap-1">
                            {canUpdate ? (
                              <button
                                type="button"
                                onClick={() => startEdit(row)}
                                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-transparent text-amber-600 transition hover:border-amber-100 hover:bg-amber-50"
                                title="Sửa"
                                aria-label="Sửa bộ phận"
                              >
                                <Pencil size={17} />
                              </button>
                            ) : null}
                            {canDelete ? (
                              <button
                                type="button"
                                onClick={() => handleDelete(row)}
                                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-transparent text-red-600 transition hover:border-red-100 hover:bg-red-50"
                                title="Xóa"
                                aria-label="Xóa bộ phận"
                              >
                                <Trash2 size={17} />
                              </button>
                            ) : null}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>
              </div>

              {/* Footer */}
              <div className="flex shrink-0 items-center justify-end border-t border-gray-100 bg-gray-50/80 px-4 py-4 sm:px-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full rounded-full border border-gray-200 bg-white px-6 py-3 text-xs font-bold uppercase tracking-wider text-gray-600 transition hover:border-gray-300 hover:bg-gray-50 sm:w-auto sm:py-2.5"
                >
                  Đóng
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
